import { useState } from 'react';
import { submitProviderFeedback, fetchProviderBase } from '../api/matchmakingApi';
import { addToNetwork, removeFromNetwork } from '@/features/network/api/networkApi';

type UseProviderFeedbackProps = {
  searchId?: number | null;
  advisorId?: string;
  clientId?: string | null;
  providerId?: string;
  providerUserId?: string;
};

export const useProviderFeedback = ({
  searchId,
  advisorId,
  clientId,
  providerId,
  providerUserId,
}: UseProviderFeedbackProps) => {
  const [isHearted, setIsHearted] = useState(false);
  const [isCrossed, setIsCrossed] = useState(false);

  const sendProviderFeedback = async (feedback: "heart" | "dislike" | "" | null) => {
    if (searchId === null || searchId === undefined || !advisorId || !clientId || !providerId) {
      return;
    }

    try {
      await submitProviderFeedback({
        searchId,
        advisorId,
        clientId,
        providerId,
        feedback,
      });
    } catch (error) {
      console.error('Error sending feedback:', error);
      throw error;
    }
  };

  const handleHeartClick = async () => {
    const nextIsHearted = !isHearted;
    setIsHearted(nextIsHearted);

    if (nextIsHearted) {
      setIsCrossed(false);
    }

    try {
      // Get the provider user ID - either from props or fetch it
      let targetUserId = providerUserId;

      if (!targetUserId && providerId) {
        // Fetch the provider base data to get the userId
        try {
          const providerBase = await fetchProviderBase(providerId);
          targetUserId = providerBase.userId;
        } catch (fetchError) {
          console.error('Error fetching provider userId:', fetchError);
        }
      }

      // Add/remove from network if we have the target user ID
      if (targetUserId) {
        if (nextIsHearted) {
          await addToNetwork(targetUserId);
        } else {
          await removeFromNetwork(targetUserId);
        }
      }

      // Also send feedback if we have all required matchmaking context
      await sendProviderFeedback(nextIsHearted ? "heart" : "");
    } catch (error) {
      console.error('Error handling heart click:', error);
      // Revert the UI state on error
      setIsHearted(!nextIsHearted);
      if (nextIsHearted) {
        setIsCrossed(true);
      }
    }
  };

  const handleCrossClick = async () => {
    const nextIsCrossed = !isCrossed;
    setIsCrossed(nextIsCrossed);
    
    if (nextIsCrossed) {
      setIsHearted(false);
    }
    
    try {
      await sendProviderFeedback(nextIsCrossed ? "dislike" : "");
    } catch (error) {
      setIsCrossed(!nextIsCrossed);
    }
  };

  return {
    isHearted,
    isCrossed,
    handleHeartClick,
    handleCrossClick,
  };
};

